import { DialogContent } from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { FormProvider } from "react-hook-form";
import { Stepper } from "@/components/Stepper";
import { getCustomProviderSteps } from "./constants";
import { useCreateProvider } from "@/queries/providers/useCreateProvider";
import { usePatchProvider } from "@/queries/providers/usePatchProvider";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { DialogTrigger } from "@/components/ui/dialog";
import { Dialog } from "@/components/ui/dialog";
import { CustomProvider } from "@/queries/providers/types";

export const CustomProvidersDialog = ({
  isOpen,
  setIsOpen,
  isEditModalOpen,
  isPreviewModalOpen,
  customProvider,
  setIsEditModalOpen,
  setIsPreviewModalOpen,
  setCustomProvider,
  isLoadingProviders,
  providers,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isEditModalOpen: boolean;
  isPreviewModalOpen: boolean;
  customProvider: CustomProvider | null;
  setIsEditModalOpen: (isEditModalOpen: boolean) => void;
  setIsPreviewModalOpen: (isPreviewModalOpen: boolean) => void;
  setCustomProvider: (customProvider: CustomProvider | null) => void;
  isLoadingProviders: boolean;
  providers: CustomProvider[];
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const methods = useForm({
    defaultValues: {
      create_custom_provider: {
        name: "",
        client: "",
        base_url: "",
        api_key: "",
      },
    },
  });
  const { mutate: createProvider, isLoading: isCreatingProvider } =
    useCreateProvider({
      onSuccess: () => {
        setIsOpen(false);
        methods.reset();
      },
    });
  const { mutate: patchProvider, isLoading: isPatchingProvider } =
    usePatchProvider({
      onSuccess: () => {
        methods.reset();
        setIsOpen(false);
        setIsEditModalOpen(false);
        setCustomProvider(null);
      },
    });

  useEffect(() => {
    if (isEditModalOpen || isPreviewModalOpen) {
      methods.setValue(
        "create_custom_provider",
        {
          name: customProvider?.name ?? "",
          client: customProvider?.client ?? "",
          base_url: customProvider?.base_url ?? "",
          api_key: "**********",
        },
        { shouldDirty: true },
      );
      setIsOpen(true);
    }
  }, [isEditModalOpen, isPreviewModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) {
      setIsOpen(false);
      setIsEditModalOpen(false);
      setIsPreviewModalOpen(false);
      setCustomProvider(null);
      methods.reset({
        create_custom_provider: {
          name: "",
          client: "",
          base_url: "",
          api_key: "",
        },
      });
      setActiveStep(0);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (data: any) => {
    if (isEditModalOpen) {
      patchProvider({
        ...data.create_custom_provider,
        id: customProvider?.id,
        api_key: data.create_custom_provider.api_key.includes("**********")
          ? undefined
          : data.create_custom_provider.api_key,
      });
    } else {
      createProvider(data.create_custom_provider);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger disabled={isLoadingProviders} asChild>
        <Button type="button" variant="outline" className="w-fit">
          Create Custom Provider
        </Button>
      </DialogTrigger>
      <DialogContent className={activeStep > 0 ? "max-w-screen-md" : ""}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stepper
              size="dialog"
              isNextButtonLoading={isCreatingProvider}
              activeStep={activeStep}
              isPreview={isPreviewModalOpen}
              steps={
                isLoadingProviders
                  ? []
                  : getCustomProviderSteps(
                      providers,
                      isPreviewModalOpen,
                      isEditModalOpen,
                    )
              }
              isLoading={isPatchingProvider || isCreatingProvider}
              onNext={() => {
                setActiveStep(activeStep + 1);
              }}
              onBack={() => {
                setActiveStep(activeStep - 1);
              }}
              onExit={() => {
                setIsOpen(false);
              }}
            />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
